import type {
  LayoutEngine,
  LayoutInput,
  LayoutResult,
  NodeBounds,
  ConnectorPath,
  BoundingRect,
} from './types';
import type { MindMapNode } from '@/model/types';
import { getStyleForLevel } from '@/theme/defaultTheme';
import { defaultTheme } from '@/theme/defaultTheme';
import { computeNodeExtraHeight } from './nodeExtraHeight';

interface SubtreeSize {
  width: number;
  height: number;
}

/**
 * Vertical tree layout (org-chart style): root at top center,
 * children arranged left-to-right below, with vertical connectors.
 */
export const verticalTreeLayout: LayoutEngine = {
  compute(input: LayoutInput): LayoutResult {
    const { nodes, rootNodeId, settings, measureText } = input;
    const root = nodes.get(rootNodeId);
    if (!root) {
      return emptyResult();
    }

    const bounds = new Map<string, NodeBounds>();
    const connectors: ConnectorPath[] = [];

    // Measure all visible nodes
    const measured = new Map<string, { width: number; height: number }>();
    for (const [id, node] of nodes) {
      if (node.position) continue;
      const depth = getDepth(node, nodes);
      const style = getStyleForLevel(defaultTheme, depth);
      const textSize = measureText(
        node.text || ' ',
        node.style?.fontSize ?? style.fontSize,
        node.style?.fontWeight ?? style.fontWeight,
      );
      let width = textSize.width + style.paddingX * 2;
      let height = textSize.height + style.paddingY * 2 + computeNodeExtraHeight(node);
      if (node.sizeOverride) {
        width = Math.max(width, node.sizeOverride.width);
        height = Math.max(height, node.sizeOverride.height);
      }
      measured.set(id, { width, height });
    }

    // Compute subtree sizes (rotated: width = horizontal span, height = vertical span)
    const subtreeSizes = new Map<string, SubtreeSize>();
    const computeSubtreeSize = (nodeId: string): SubtreeSize => {
      const node = nodes.get(nodeId);
      const size = measured.get(nodeId) ?? { width: 100, height: 30 };
      if (!node || node.collapsed || node.childIds.length === 0) {
        const result = { width: size.width, height: size.height };
        subtreeSizes.set(nodeId, result);
        return result;
      }

      const visibleKids = node.childIds.filter((id) => nodes.has(id));
      let childrenWidth = 0;
      let maxChildHeight = 0;
      for (const childId of visibleKids) {
        const childSize = computeSubtreeSize(childId);
        childrenWidth += childSize.width;
        maxChildHeight = Math.max(maxChildHeight, childSize.height);
      }
      childrenWidth += Math.max(0, visibleKids.length - 1) * settings.horizontalGap;

      const result = {
        width: Math.max(size.width, childrenWidth),
        height: size.height + settings.verticalGap + maxChildHeight,
      };
      subtreeSizes.set(nodeId, result);
      return result;
    };

    computeSubtreeSize(rootNodeId);

    // Position root at top center (centered on x=0)
    const rootSize = measured.get(rootNodeId) ?? { width: 120, height: 40 };
    bounds.set(rootNodeId, {
      id: rootNodeId,
      x: -rootSize.width / 2,
      y: -rootSize.height / 2,
      width: rootSize.width,
      height: rootSize.height,
      centerX: 0,
      centerY: 0,
      depth: 0,
    });

    // Position children recursively
    const positionChildren = (
      parentId: string,
      childIds: string[],
      parentBounds: NodeBounds,
    ) => {
      if (childIds.length === 0) return;

      // Total width of all child subtrees
      let totalWidth = 0;
      for (const childId of childIds) {
        const subtreeSize = subtreeSizes.get(childId) ?? { width: 100, height: 30 };
        totalWidth += subtreeSize.width;
      }
      totalWidth += Math.max(0, childIds.length - 1) * settings.horizontalGap;

      // Start from the left, centered under the parent
      let currentX = parentBounds.centerX - totalWidth / 2;

      for (const childId of childIds) {
        const childNode = nodes.get(childId);
        if (!childNode) continue;

        const childSize = measured.get(childId) ?? { width: 100, height: 30 };
        const subtreeSize = subtreeSizes.get(childId) ?? { width: 100, height: 30 };
        const depth = getDepth(childNode, nodes);

        // Center the child node within its subtree allocation
        const childCenterX = currentX + subtreeSize.width / 2;
        const childY = parentBounds.y + parentBounds.height + settings.verticalGap;

        const childBounds: NodeBounds = {
          id: childId,
          x: childCenterX - childSize.width / 2,
          y: childY,
          width: childSize.width,
          height: childSize.height,
          centerX: childCenterX,
          centerY: childY + childSize.height / 2,
          depth,
        };
        bounds.set(childId, childBounds);

        // Connector: bottom of parent to top of child
        const fromX = parentBounds.centerX;
        const fromY = parentBounds.y + parentBounds.height;
        const toX = childBounds.centerX;
        const toY = childBounds.y;

        connectors.push({
          parentId,
          childId,
          path: verticalBezierPath(
            { x: fromX, y: fromY },
            { x: toX, y: toY },
          ),
        });

        // Recurse
        if (!childNode.collapsed) {
          const grandchildren = childNode.childIds.filter((id) => nodes.has(id));
          positionChildren(childId, grandchildren, childBounds);
        }

        currentX += subtreeSize.width + settings.horizontalGap;
      }
    };

    const rootBounds = bounds.get(rootNodeId)!;
    const visibleChildren = root.collapsed
      ? []
      : root.childIds.filter((id) => nodes.has(id));
    positionChildren(rootNodeId, visibleChildren, rootBounds);

    // Apply manual position overrides and handle disconnected nodes
    let hasOverrides = false;
    for (const [id, node] of nodes) {
      if (!node.position) continue;
      if (hasCollapsedAncestor(node, nodes)) {
        bounds.delete(id);
        continue;
      }
      hasOverrides = true;
      const size = measured.get(id) ?? { width: 100, height: 30 };
      const existing = bounds.get(id);
      bounds.set(id, {
        id,
        x: node.position.x,
        y: node.position.y,
        width: size.width,
        height: size.height,
        centerX: node.position.x + size.width / 2,
        centerY: node.position.y + size.height / 2,
        depth: existing?.depth ?? 0,
      });
    }

    // Rebuild connectors from final bounds if any positions were overridden
    if (hasOverrides) {
      connectors.length = 0;
      for (const [id, node] of nodes) {
        if (!node.parentId) continue;
        const childB = bounds.get(id);
        const parentB = bounds.get(node.parentId);
        if (!childB || !parentB) continue;
        connectors.push({
          parentId: node.parentId,
          childId: id,
          path: verticalBezierPath(
            { x: parentB.centerX, y: parentB.y + parentB.height },
            { x: childB.centerX, y: childB.y },
          ),
        });
      }
    }

    const totalBounds = computeTotalBounds(bounds);
    return { nodeBounds: bounds, connectorPaths: connectors, totalBounds };
  },
};

/** Vertical cubic bezier (control points shift in Y direction). */
function verticalBezierPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
): string {
  const dy = to.y - from.y;
  const cp1y = from.y + dy * 0.5;
  const cp2y = to.y - dy * 0.5;
  return `M ${from.x} ${from.y} C ${from.x} ${cp1y}, ${to.x} ${cp2y}, ${to.x} ${to.y}`;
}

function getDepth(node: MindMapNode, nodes: Map<string, MindMapNode>): number {
  let depth = 0;
  let current = node;
  while (current.parentId) {
    depth++;
    const parent = nodes.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return depth;
}

function hasCollapsedAncestor(node: MindMapNode, nodes: Map<string, MindMapNode>): boolean {
  let current = node;
  while (current.parentId) {
    const parent = nodes.get(current.parentId);
    if (!parent) return false;
    if (parent.collapsed) return true;
    current = parent;
  }
  return false;
}

function computeTotalBounds(bounds: Map<string, NodeBounds>): BoundingRect {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const b of bounds.values()) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function emptyResult(): LayoutResult {
  return { nodeBounds: new Map(), connectorPaths: [], totalBounds: { x: 0, y: 0, width: 0, height: 0 } };
}
